import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Archive, Clock, Trash2, AlertTriangle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Super-admin 3-option customer deletion dialog.
 *
 * Props:
 *   open, onOpenChange
 *   customer     : { user_id, user_name, user_email }
 *   headers      : axios auth headers
 *   isMasterAdmin: boolean — hides OPTION 2 & 3 for regular admins
 *   onDeleted    : () => void  — fired after any successful action so parent can refresh
 */
const DeleteCustomerDialog = ({ open, onOpenChange, customer, headers, isMasterAdmin, onDeleted }) => {
  const [tab, setTab] = useState('archive');
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [safety, setSafety] = useState(null);

  useEffect(() => {
    if (!open || !customer?.user_id) return;
    setReason('');
    setConfirmText('');
    setTab(isMasterAdmin ? 'archive' : 'archive');
    // Load safety counts once
    axios.get(`${API_URL}/api/admin/crm/clients/${customer.user_id}/delete-safety`, { headers })
      .then(r => setSafety(r.data))
      .catch(() => setSafety(null));
  }, [open, customer?.user_id, isMasterAdmin, headers]);

  if (!customer) return null;

  const call = async (fn) => {
    setBusy(true);
    try {
      await fn();
      onDeleted?.();
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = 'Action failed';
      if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object') msg = detail.message || msg;
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const doArchive = () => call(async () => {
    await axios.post(`${API_URL}/api/admin/crm/clients/${customer.user_id}/archive`, {}, { headers });
    toast.success(`Archived ${customer.user_name || 'customer'}`);
  });

  const doSoftDelete = () => call(async () => {
    await axios.post(
      `${API_URL}/api/admin/crm/clients/${customer.user_id}/soft-delete`,
      { reason: reason || null },
      { headers },
    );
    toast.success(`Moved ${customer.user_name || 'customer'} to recycle bin (30 days)`);
  });

  const doPermanentDelete = () => call(async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      throw new Error('confirmation required');
    }
    await axios.post(
      `${API_URL}/api/admin/crm/clients/${customer.user_id}/permanent-delete`,
      { confirm: 'DELETE' },
      { headers },
    );
    toast.success(`Permanently deleted ${customer.user_name || 'customer'}`);
  });

  const counts = safety?.counts || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="delete-customer-dialog">
        <DialogHeader>
          <DialogTitle>Delete &quot;{customer.user_name || 'customer'}&quot;</DialogTitle>
          <DialogDescription>
            Choose how you&apos;d like to handle this customer. Only master admins can permanently delete.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className={`grid w-full ${isMasterAdmin ? 'grid-cols-3' : 'grid-cols-1'}`}>
            <TabsTrigger value="archive" data-testid="tab-archive">
              <Archive className="w-4 h-4 mr-1.5" /> Archive
            </TabsTrigger>
            {isMasterAdmin && (
              <TabsTrigger value="soft" data-testid="tab-soft-delete">
                <Clock className="w-4 h-4 mr-1.5" /> Delete (30d)
              </TabsTrigger>
            )}
            {isMasterAdmin && (
              <TabsTrigger value="permanent" data-testid="tab-permanent" className="text-red-600 data-[state=active]:text-red-700">
                <Trash2 className="w-4 h-4 mr-1.5" /> Permanent
              </TabsTrigger>
            )}
          </TabsList>

          {/* OPTION 1 — ARCHIVE */}
          <TabsContent value="archive" className="mt-4 space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
              <p className="font-semibold mb-1">Archive customer</p>
              <p>Keeps ALL records intact. The customer disappears from active lists and search but every invoice, appointment, note, and payment history stays exactly where it is. Restore anytime.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button onClick={doArchive} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-archive">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
                Archive Customer
              </Button>
            </div>
          </TabsContent>

          {/* OPTION 2 — SOFT DELETE (30-day recovery) */}
          {isMasterAdmin && (
            <TabsContent value="soft" className="mt-4 space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                <p className="font-semibold mb-1">Delete with 30-day recovery</p>
                <p>Removes the customer + all associated invoices, appointments, photos, notes, addresses and history from every view. Records stay in a hidden recycle bin for <b>30 days</b>, then are permanently purged. You can restore during the 30-day window from <b>Admin → Recycle Bin</b>.</p>
              </div>
              {safety && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <StatChip label="Invoices" value={(counts.admin_invoices || 0) + (counts.all_invoices || 0)} />
                  <StatChip label="Appointments" value={counts.all_appointments || 0} />
                  <StatChip label="Paid $" value={counts.paid_invoices || 0} highlight={(counts.paid_invoices || 0) > 0} />
                  <StatChip label="Card on file" value={counts.stripe_customer ? 'Yes' : 'No'} highlight={counts.stripe_customer > 0} />
                </div>
              )}
              <div>
                <Label className="text-xs">Reason (optional, for audit log)</Label>
                <Textarea value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="e.g. duplicate customer, spam signup, requested off-boarding..."
                  rows={2} data-testid="soft-delete-reason" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
                <Button onClick={doSoftDelete} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-soft-delete">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
                  Move to Recycle Bin
                </Button>
              </div>
            </TabsContent>
          )}

          {/* OPTION 3 — PERMANENT DELETE */}
          {isMasterAdmin && (
            <TabsContent value="permanent" className="mt-4 space-y-3">
              <div className="bg-red-50 border border-red-300 rounded-lg p-4 text-sm text-red-900">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="font-bold">Permanent deletion — cannot be undone</p>
                </div>
                <p className="mb-2">This will immediately and irreversibly destroy the customer plus every operational record:</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>Customer profile + all addresses</li>
                  <li>Every invoice (paid, sent, draft, cancelled)</li>
                  <li>Every appointment + past clean + job photos + QC items</li>
                  <li>Internal notes + change requests + timeline</li>
                  <li>Referrals + card-capture links + recurring plans</li>
                </ul>
                <p className="mt-2 text-xs">Stripe records are NOT touched — void invoices in Stripe first if needed.</p>
              </div>
              {safety && (counts.paid_invoices > 0 || counts.stripe_customer > 0) && (
                <div className="bg-red-100 border border-red-400 rounded-lg p-3 text-xs text-red-900">
                  <p className="font-semibold">⚠ This customer has business records:</p>
                  <p>{counts.paid_invoices || 0} paid invoice(s){counts.stripe_customer ? ', card on Stripe file' : ''}. Consider archiving instead.</p>
                </div>
              )}
              <div>
                <Label className="text-xs text-red-700">Type <b>DELETE</b> to confirm</Label>
                <Input
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="font-mono"
                  data-testid="permanent-delete-confirm-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
                <Button
                  onClick={doPermanentDelete}
                  disabled={busy || confirmText !== 'DELETE'}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-permanent-delete"
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Permanently Delete
                </Button>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const StatChip = ({ label, value, highlight = false }) => (
  <div className={`px-3 py-2 rounded-lg border ${highlight ? 'bg-red-50 border-red-200' : 'bg-zinc-50 border-zinc-200'}`}>
    <p className="text-[10px] uppercase font-semibold text-zinc-500">{label}</p>
    <p className={`text-sm font-bold ${highlight ? 'text-red-700' : 'text-zinc-900'}`}>{value}</p>
  </div>
);

export default DeleteCustomerDialog;
